import * as React from "react";
import { useParams } from "react-router-dom";
import fileDownload from "js-file-download";
import Axios from "axios";
import {
  Layout,
  Button,
  Space,
  Descriptions,
  Spin,
  Skeleton,
  Row,
  Col,
  Input,
  Tree,
  message,
  Progress,
} from "antd";

import { ResultApiCommunicator } from "api/communicators/ResultApiCommunicator";
import { UploadCommunicator } from "api/communicators/UploadCommunicator";
import { ListPageLayout } from "layouts";
import { TableListCellItems } from "components/Model/TableListCellItems";
import { ScanGrid } from "components/ScanGrid";
import { convertDataToAntdTree } from "components/utils";

const { Header } = Layout;
const { TextArea } = Input;

/**
 * PrintResultDetailPage Component
 * 
 * Displays detailed information about a specific print result and its associated data.
 * Provides functionality for viewing, editing, and managing print result details including
 * file uploads, scan data, and result metadata.
 * 
 * Features:
 * - Complete print result information display
 * - File upload capabilities with progress tracking
 * - Scan data visualization
 * - Result metadata editing
 * - File download capabilities
 * - Real-time updates via Amplify subscriptions
 * - File tree visualization
 * - Status tracking and display
 * 
 * @returns {JSX.Element} The print result detail page component
 */
export const PrintResultDetailPage = () => {
  const { modelId, printResultId } = useParams();
  return (
    <div>
      <Layout>
        <Header className="header">
          <div className="logo" />
        </Header>
      </Layout>
      <ResultUpload modelId={modelId} printResultId={printResultId} />
    </div>
  );
};

/**
 * ResultUpload Component
 * 
 * Internal component that handles the main print result detail functionality including
 * data fetching, state management, and user interactions. This component manages
 * all the complex state and API interactions for the print result detail view.
 * 
 * @param {Object} props - Component props
 * @param {string} props.modelId - The ID of the print file (model)
 * @param {string} props.printResultId - The ID of the specific print result to display
 * @returns {JSX.Element} The result upload component
 */
function ResultUpload({ modelId, printResultId }) {
  const [messageApi, contextHolder] = message.useMessage();
  const [resultApiCommunicator] = React.useState(new ResultApiCommunicator());
  const [uploadCommunicator] = React.useState(new UploadCommunicator());
  const [model, setModel] = React.useState();
  const [loading, setLoading] = React.useState({
    active: false,
    message: "Loading...",
  });
  const [uploading, setUploading] = React.useState(false);
  const [uploadingPercent, setUploadingPercent] = React.useState(0);

  const firstPrintResult = React.useMemo(() => {
    if (!model || model.printResults.length === 0) {
      return {};
    }
    return model.printResults[0];
  }, [model]);

  /**
   * The fields of a printResult are modifiable via
   * input fields on this page, once the data has been retrieved, we
   * statically set the fields, and allow them to be modified.
   * Keep a central state object to contain them all and manage dispatching
   * update to API.
   */
  const [
    modifiablePrintResultInputFields,
    setModifiablePrintResultInputFields,
  ] = React.useState({
    name: "",
    printNotes: "",
    printResultNotes: "",
    printRecipient: "",
    printRecipientNotes: "",
  });

  const doFetch = React.useCallback(async () => {
    setLoading({ message: "Loading...", active: true });
    try {
      const res = await resultApiCommunicator.fetchById(modelId, printResultId);
      const model = res.data;
      setModel(res.data);
      if (model.printResults.length > 0) {
        const pr = res.data.printResults[0];
        setModifiablePrintResultInputFields({
          name: pr.name,
          printNotes: pr.printNotes,
          printResultNotes: pr.printResultNotes,
          printRecipient: pr.printRecipient,
          printRecipientNotes: pr.printRecipientNotes,
        });
      }
    } catch (err) {
      messageApi.error(err.message);
    }
    setLoading((s) => ({ ...s, active: false }));
  }, [setLoading, resultApiCommunicator, modelId, printResultId, messageApi]);

  React.useEffect(() => {
    doFetch();
  }, [doFetch]);

  const updateNotes = React.useCallback(async () => {
    // Get the selected file name
    setLoading({ message: "Loading...", active: true });
    try {
      resultApiCommunicator.update({
        modelId,
        printResultId,
        ...modifiablePrintResultInputFields,
      });
    } catch (e) {
      setLoading({ active: false, message: "" });
      messageApi.error(e.message);
    }
    setTimeout(doFetch, 5000);
  }, [
    doFetch,
    resultApiCommunicator,
    modelId,
    printResultId,
    messageApi,
    modifiablePrintResultInputFields,
  ]);

  const downloadOrigModelClick = React.useCallback(async () => {
    setLoading({ active: true, message: "Downloading..."});
    const fileDownloadRes = await Axios.get(model.downloadUrl, {
      responseType: "blob",
    });
    setLoading((s) => ({ ...s, active: false }));
    fileDownload(fileDownloadRes.data, model.gcPrintProjectName);
  }, [model, setLoading]);

  const downloadScannedModelClick = React.useCallback(async () => {
    setLoading({ active: true, message: "Downloading..."});
    const fileDownloadRes = await Axios.get(firstPrintResult.downloadUrl, {
      responseType: "blob",
    });
    setLoading((s) => ({ ...s, active: false }));
    fileDownload(fileDownloadRes.data, firstPrintResult.name + ".print");
  }, [firstPrintResult, setLoading]);

  const modelDetailItems = React.useMemo(() => {
    if (!model) {
      return [];
    }
    return [
      {
        key: "request-name",
        label: "request-name",
        children: (
          <Input
            placeholder="Request label"
            value={modifiablePrintResultInputFields.name}
            onChange={(e) =>
              setModifiablePrintResultInputFields((s) => ({
                ...s,
                name: e.target.value,
              }))
            }
          />
        ),
      },
      {
        key: "orig-project-name",
        label: "Original Project Name",
        children: model.gcPrintProjectName,
      },
      {
        key: "print-result-status",
        label: "Status",
        children: (
          <TableListCellItems.StatusBadge status={firstPrintResult.status} />
        ),
      },
      {
        key: "original-metadata",
        label: "Original Metadata",
        children: (
          <Tree
            treeData={convertDataToAntdTree(model.manifestMetadata)}
            height={233}
          />
        ),
        span: 1,
      },
      {
        key: "upload-metadata",
        label: "Result Metadata",
        children: (
          <Tree
            treeData={convertDataToAntdTree(firstPrintResult.manifestMetadata)}
            height={233}
          />
        ),
        span: 2,
      },
      {
        key: "file-for-printing",
        label: "File for printing",
        children: (
          <Button type="link" onClick={downloadOrigModelClick}>
            Download - {model.gcPrintProjectName}
          </Button>
        ),
        span: 1,
      },
      {
        key: "file-for-printing",
        label: "Scanned Result",
        children: (
          <Button type="link" onClick={downloadScannedModelClick}>
            Download - {modifiablePrintResultInputFields.name}.print
          </Button>
        ),
        span: 2,
      },
      {
        key: "4",
        label: "Print Notes",
        children: (
          <TextArea
            placeholder="Notes for printing"
            rows={4}
            value={modifiablePrintResultInputFields.printNotes}
            onChange={(e) =>
              setModifiablePrintResultInputFields((s) => ({
                ...s,
                printNotes: e.target.value,
              }))
            }
          />
        ),
        span: 1,
      },
      {
        key: "5",
        label: "Print Recipient",
        children: (
          <TextArea
            placeholder="Shipping and other information for the person that will be scanning"
            rows={4}
            value={modifiablePrintResultInputFields.printRecipient}
            onChange={(e) =>
              setModifiablePrintResultInputFields((s) => ({
                ...s,
                printRecipient: e.target.value,
              }))
            }
          />
        ),
        span: 2,
      },
      {
        key: "post-print-notes",
        label: "Post Printing Notes",
        children: (
          <TextArea
            placeholder="Post Printing Notes: Add notes about print (shipping info, etc.)"
            rows={4}
            value={modifiablePrintResultInputFields.printResultNotes}
            onChange={(e) =>
              setModifiablePrintResultInputFields((s) => ({
                ...s,
                printResultNotes: e.target.value,
              }))
            }
          />
        ),
        span: 1,
      },
      {
        key: "scan-notes",
        label: "Scan Notes",
        children: (
          <TextArea
            placeholder="Post Printing Notes: Add notes about print (shipping info, etc.)"
            rows={4}
            value={modifiablePrintResultInputFields.printRecipientNotes}
            onChange={(e) =>
              setModifiablePrintResultInputFields((s) => ({
                ...s,
                printRecipientNotes: e.target.value,
              }))
            }
          />
        ),
        span: 2,
      },
    ];
  }, [
    model,
    modifiablePrintResultInputFields,
    downloadOrigModelClick,
    downloadScannedModelClick,
    firstPrintResult,
  ]);

  const updateUploadProgress = React.useCallback(async (progressEvent) => {
    //console.log('progressEvent', progressEvent);
    if(progressEvent.event.lengthComputable) {
      const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      setUploadingPercent(percent);
    }
  }, [setUploadingPercent]);

  const scanFileUpload = React.useCallback(async (file, occurrenceId) => {
    // console.log('file', file);
    // console.log('occurrenceId', occurrenceId);
    const fileName = file.name;
    setLoading({ message:"Uploading: " + fileName, active: true });
    setUploadingPercent(0);
    setUploading(true);


    let uploadUrl;
    try {
      const uploadUrlRes = await resultApiCommunicator.getScanUploadUrl(firstPrintResult.token, occurrenceId, fileName);
      // console.log('uploadUrlRes', uploadUrlRes);
      uploadUrl = uploadUrlRes.data.uploadUrl;
      // console.log('uploadUrl', uploadUrl);
    } catch(e) {
      console.error(e);
      setLoading({active: false});
      messageApi.error("Error uploading file " + file.name);
    }

    try {
      await uploadCommunicator.uploadFile(uploadUrl, file, updateUploadProgress);
      setLoading({ message:"Finishing upload..." + fileName, active: true });
    } catch (e) {
      console.error(e);
      setLoading({active: false});
      messageApi.error("Error uploading file " + file.name);
    }

    setTimeout(() => {
      setUploading(false);
      setUploadingPercent(0);
      doFetch();
    }, 5000);
  }, [setLoading, setUploadingPercent, doFetch, firstPrintResult.token, messageApi, resultApiCommunicator, uploadCommunicator, updateUploadProgress]);
    

  return (
    <ListPageLayout>
      {contextHolder}
      {loading.active && (
        <span>
          <Spin /> {loading.message}
        </span>
      )}
      {uploading && <span><Progress percent={uploadingPercent} /></span>}
      {!loading.active && !model && (
        <div>
          <span>Please make sure your link is correct</span>
        </div>
      )}
      <Skeleton loading={loading.active}>
        <div>
          <Space
            direction="vertical"
            size="middle"
            style={{
              display: "flex",
            }}
          >
            <h3>Request Information</h3>
            <Descriptions bordered items={modelDetailItems} />
            {!loading.active && (
              <Space
                direction="vertical"
                size="middle"
                style={{
                  display: "flex",
                }}
              >
                <Row>
                  <Col>
                    <Space>
                      <Button type="primary" onClick={() => updateNotes()}>
                        {" "}
                        Update
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
            )}
            <h3>Parts</h3>
            <ScanGrid scans={firstPrintResult.scanUploads} onFileSelect={scanFileUpload} />
          </Space>
        </div>
      </Skeleton>
    </ListPageLayout>
  );
}
